import {
  ANALYTICS_EVENT,
  BookingMode,
  AVAILABLE_BRAND_FEATURES as BrandFeature,
} from '@kouto/types';
import { validateAccessCode } from 'actions/access-code';
import { useDispatch } from 'AppProvider';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import BottomSheetResourcePage from 'components/Resource/BottomSheetResourcePage';
import { ContinuousSessionPicker } from 'components/molecules/ContinuousSessionPicker';
import { ResourceDetailContainer } from 'components/Resource/ResourceDetailContainer';
import {
  analyticsManager,
  getAnalyticsDataFromCartItem,
} from 'features/analytics';
import { useResourceMapContext } from 'features/Reserve/contexts/ResourceMapContext';
import useAvailableDates from 'features/Reserve/hooks/useAvailableDates';
import useContinuousOptions from 'hooks/useContinuousOptions';
import {
  describeStayLength,
  isDayBasedTimeFrame,
} from 'utils/continuousSelection';
import useStayQuote from 'hooks/useStayQuote';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { useTierParticipants } from 'hooks/use-selected-participants';
import { useCart } from 'hooks/useCart';
import moment from 'moment';
import { isEmpty } from 'ramda';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { VALIDATE_ACCESS_CODE_FAILURE } from 'types/access-code';
import NotFoundExperience from 'components/NotFoundExperience/NotFoundExperience';

import { CartItemState } from 'types/cart';
import { TIME_FORMAT, DATE_FORMAT } from 'utils';
import type { ITax } from 'types/experience.types';
import { useIsMobile } from 'WindowDimensionProvider';
import useCustomQuestions from 'api/experience/useCustomQuestions/useCustomQuestions';
import hasParticipantOrCustomQuestion from 'utils/customQuestions/hasParticipantOrCustomQuestion';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import useBookableAddOns from 'api/listing/useBookableAddOns';
import useResourceBookingSession from '../hooks/useResourceBookingSession';
import { ResourcePageSkeleton } from './Skeleton';

export const ResourcePage = () => {
  const {
    availabilityRule,
    collectionId,
    continuousSelection,
    date,
    isContinuous,
    isInitialLoading,
    isLoading,
    isLoadingListing,
    latest,
    listing,
    listingError,
    resource,
    resourceGroup,
    selectedSession,
    sessions,
    setResourceBookingSessionParams,
    timeFrame,
  } = useResourceBookingSession();
  const languageCode = useSupportedLanguage();
  const isMobileView = useIsMobile();
  const { focusResources } = useResourceMapContext();
  const [, setSelectedTierCustomers] = useTierParticipants();
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();

  const dispatch = useDispatch();
  const [accessCodeError, setAccessCodeError] = useState('');
  const [isCodeValidated, setIsAccessCodeValidated] = useState(false);
  // Ref, not state: the validated code is read later in the same click handler
  // that triggered validation, before the next render.
  const validatedAccessCode = useRef<{
    code: string;
    cartItemMetadata?: { experienceIds?: string[] };
  } | null>(null);

  const isAccessCodeVerified = listing?.isExclusive ? isCodeValidated : true;
  const isMapped = listing?.isMapped ?? false;
  const hasBookableAddOns = useBookableAddOns({
    listingId: listing?.id,
    experienceId: resource?.id,
    startDateTime: selectedSession?.startDateTime,
    sessionDuration: selectedSession?.duration,
    hasAddOns: !!listing?.addOns?.length,
  });

  const [tierId, setTierId] = useState(selectedSession?.priceTiers?.[0].id);
  const { onAddToCart } = useCart();
  const isCartFeatureEnabled = useBrandToggleFeature(
    BrandFeature.SHOPPING_CART,
  );

  const { data: customQuestions, isLoading: isLoadingCustomQuestions } =
    useCustomQuestions({
      brandId: listing?.brandId,
      experienceId: resource?.id,
      includeDefault: true,
      language: languageCode,
    });

  const { hasCustomQuestions, askParticipantsInfo } = useMemo(() => {
    return hasParticipantOrCustomQuestion(customQuestions ?? []);
  }, [customQuestions]);

  useEffect(() => {
    focusResources([]);
  }, []);

  const deselectResource = useCallback(() => {
    setResourceBookingSessionParams({
      resourceId: undefined,
    });
  }, [setResourceBookingSessionParams]);

  useEffect(() => {
    if (!tierId && selectedSession?.priceTiers) {
      setTierId(selectedSession.priceTiers?.[0].id);
    }
  }, [tierId, selectedSession]);

  const handleSessionChange = useCallback(
    (time: string | undefined, duration: number) => {
      setResourceBookingSessionParams({
        time,
        duration,
      });
    },
    [setResourceBookingSessionParams],
  );

  // A day-based stay is picked on a two-month calendar that needs to know which dates are booked out.
  const [calendarMonth, setCalendarMonth] = useState<moment.Moment>(() =>
    moment(date ?? undefined),
  );
  const isDayBased = isDayBasedTimeFrame(timeFrame);
  const { availableDates, isLoadingAvailableDates } = useAvailableDates({
    collectionId: listing?.resourceGroupCollectionId,
    experienceId: resource?.id,
    from: calendarMonth.clone().startOf('month').format(DATE_FORMAT),
    latest,
    skip: !isContinuous || !isDayBased,
    to: calendarMonth
      .clone()
      .add(1, 'month')
      .endOf('month')
      .format(DATE_FORMAT),
  });

  const handleStayChange = useCallback(
    (nextDate: string, duration: number) => {
      setResourceBookingSessionParams({
        date: nextDate,
        duration,
        time: undefined,
      });
    },
    [setResourceBookingSessionParams],
  );

  const handleMonthChange = useCallback((month: Date) => {
    setCalendarMonth(moment(month));
  }, []);

  const { durationOptions, onDurationSelect, onStartSelect, startOptions } =
    useContinuousOptions({
      date,
      durations: resourceGroup?.durations ?? [],
      experienceId: resource?.id,
      onChange: handleSessionChange,
      rule: availabilityRule,
      selection: continuousSelection,
      spans: sessions,
      timeFrame,
    });

  // The picked length priced by the server, so the tier reads as what it costs for that length — as it
  // does on the group page — and moves with the start and the length.
  const stayQuoteTiers = useMemo(
    () =>
      (selectedSession?.priceTiers ?? []).map((tier) => ({
        id: tier.id,
        name: tier.name,
        participants: tier.id === tierId ? 1 : 0,
      })),
    [selectedSession?.priceTiers, tierId],
  );
  const { quote: stayQuote } = useStayQuote({
    enabled: isContinuous,
    experienceId: resource?.id,
    selection: continuousSelection,
    tiers: stayQuoteTiers,
  });
  const stayPricing = useMemo(() => {
    if (!stayQuote || !continuousSelection || !timeFrame) {
      return null;
    }
    return {
      label: translate('forLength', {
        length: describeStayLength(
          continuousSelection,
          timeFrame,
          translate,
          locale,
        ),
      }),
      priceByTierName: stayQuote.stayPriceByTierName,
    };
  }, [continuousSelection, locale, stayQuote, timeFrame, translate]);

  // Only the first paint waits behind the skeleton; picking another date keeps the panel up, with the
  // previous rows, while the next arrive.
  if (isInitialLoading) {
    return <ResourcePageSkeleton />;
  }

  // Show 404 if:
  // 1. Has real errors (collectionError)
  // 2. Finished loading collection AND collectionId exists BUT no collection found
  if (listingError || (!isLoadingListing && collectionId && !listing)) {
    return <NotFoundExperience />;
  }

  // After loading, if essential data is missing, show 404. A scheduled resource needs its session too;
  // a continuously booked one renders without a selection, so the guest can pick a stay that is free.
  if (
    !listing ||
    !resourceGroup ||
    !resource ||
    (!selectedSession && !isContinuous)
  ) {
    return <>{translate('noData')}</>;
  }

  const syncToCart = async (
    route = '',
    state = CartItemState.READY,
    overrideSearch = '',
  ) => {
    if (!tierId || !selectedSession) {
      return;
    }

    const priceTier = selectedSession.priceTiers?.find((t) => t.id === tierId);

    if (!priceTier) {
      return;
    }

    const participants = [
      {
        fullName: 'Resource Purchaser',
        firstName: 'Resource',
        lastName: 'Purchaser',
        terms: false,
        price: priceTier.price,
        priceTierName: priceTier.name || '',
      },
    ];

    setSelectedTierCustomers({
      currentExperience: resource.id,
      selectedParticipants: [{ ...priceTier, selectedNumber: 1 }],
    });

    const cartData = {
      additionalCustomQuestionResponses: {},
      bookingMode: BookingMode.SHARED,
      experienceId: resource?.id,
      groupId: resourceGroup.id,
      groupTitle: resourceGroup.title ?? undefined,
      listingId: listing.id,
      maxParticipantReached: '',
      notes: '',
      participants,
      resourceGroupCollectionId:
        listing?.resourceGroupCollectionId ?? undefined,
      sessionDateTime: `${date}T${
        selectedSession.startDateTime
          ? moment(selectedSession.startDateTime).format(TIME_FORMAT)
          : undefined
      }`,
      sessionDuration: selectedSession.duration
        ? moment.duration(selectedSession.duration, 'minutes').toISOString()
        : '',
      timezone: listing?.location?.timezone,
      state,
      ...(validatedAccessCode.current?.cartItemMetadata?.experienceIds?.includes(
        resource.id,
      ) && { accessCode: validatedAccessCode.current.code }),
    };

    if (state === CartItemState.READY) {
      const data = getAnalyticsDataFromCartItem({
        cartData,
        experienceId: resource?.id,
        listing,
        locationName: listing?.location?.name,
        priceByTierName: stayPricing?.priceByTierName,
        resourceGroupTitle: resourceGroup.title ?? undefined,
      });

      analyticsManager.sendEvent(
        ANALYTICS_EVENT.CLICK_ADD_TO_CART_BUTTON,
        data,
      );
      analyticsManager.sendEvent(
        ANALYTICS_EVENT.CLICK_PROCEED_TO_CHECKOUT_BUTTON,
        data,
      );
    }
    await onAddToCart(cartData, route, overrideSearch);
  };

  const handleAddToCart = async () => {
    await syncToCart();
    setResourceBookingSessionParams({
      resourceId: undefined,
    });
  };

  const handleOnCheckout = async () => {
    await syncToCart('/checkout');
  };

  const hasAddOns = listing?.addOns?.length > 0;

  const withAccessCodeValidation =
    (onSuccess: CallableFunction) => async (code: string) => {
      if (!listing.isExclusive) {
        onSuccess();
        return;
      }

      if (isEmpty(code)) {
        setAccessCodeError('Access code is required');
        return;
      }
      const response = await dispatch(
        validateAccessCode({
          brandId: listing?.brandId,
          code,
          experienceId: resource.id,
          isCartFeatureEnabled,
        }),
      );
      if (response.type === VALIDATE_ACCESS_CODE_FAILURE) {
        setIsAccessCodeValidated(false);
        setAccessCodeError('Invalid code');
        return;
      }
      validatedAccessCode.current = response.payload?.accessCode ?? null;
      onSuccess();
    };

  const onContinue =
    hasAddOns || hasCustomQuestions || askParticipantsInfo
      ? async () => {
          if (hasAddOns && (await hasBookableAddOns())) {
            await syncToCart('/addons/{cartItemId}', CartItemState.PENDING);
          } else if (hasCustomQuestions || askParticipantsInfo) {
            const params = new URLSearchParams();
            params.set(
              'skipParticipantsName',
              askParticipantsInfo ? 'false' : 'true',
            );
            if (date) {
              params.set('date', date);
            }
            if (latest) {
              params.set('latest', 'true');
            }
            await syncToCart(
              `/e/${resource.id}/${
                isCartFeatureEnabled ? 'participants' : 'booking'
              }/{cartItemId}`,
              CartItemState.PENDING,
              params.toString(),
            );
          } else {
            await handleOnCheckout();
          }
        }
      : undefined;

  if (!isMapped) {
    return null;
  }

  if (isMobileView) {
    // The bottom sheet reads the session's date and time back to the guest, so it needs one.
    if (!selectedSession) {
      return <>{translate('noData')}</>;
    }
    return (
      <BottomSheetResourcePage
        open={!!resource}
        isLoading={isLoading}
        onClose={() => deselectResource()}
        heading={resource.title}
        customCallToAction={listing?.customCallToAction ?? null}
        size="full"
        priceTiers={selectedSession.priceTiers || []}
        taxes={resourceGroup.experiences?.[0]?.taxes as unknown as ITax[]}
        selectedPriceTierId={tierId}
        stayPricing={stayPricing}
        onTierSelect={setTierId}
        selectedSession={selectedSession}
        onAddToCart={handleAddToCart}
        onCheckout={handleOnCheckout}
        onContinue={onContinue}
        isLoadingCustomQuestions={isLoadingCustomQuestions}
        accessCodeVerified={isAccessCodeVerified}
        accessCodeError={accessCodeError}
        onValidateAccessCode={withAccessCodeValidation(() => {
          setIsAccessCodeValidated(true);
        })}
        sessionPicker={
          isContinuous &&
          availabilityRule &&
          timeFrame &&
          date &&
          isDayBased ? (
            <ContinuousSessionPicker
              availableDates={availableDates}
              date={date}
              durationOptions={durationOptions}
              isLoadingAvailableDates={isLoadingAvailableDates}
              onDurationSelect={onDurationSelect}
              onMonthChange={handleMonthChange}
              onRangeChange={handleStayChange}
              onStartSelect={onStartSelect}
              rule={availabilityRule}
              selection={continuousSelection}
              startOptions={startOptions}
              timeFrame={timeFrame}
            />
          ) : undefined
        }
      />
    );
  }

  return (
    <ResourceDetailContainer
      accessCodeError={accessCodeError}
      availableDates={availableDates}
      collectionCustomCallToAction={listing?.customCallToAction ?? null}
      durations={resourceGroup.durations ?? []}
      isExclusive={listing?.isExclusive}
      isLoadingAvailableDates={isLoadingAvailableDates}
      isLoadingCustomQuestions={isLoadingCustomQuestions}
      onAddToCart={withAccessCodeValidation(handleAddToCart)}
      onCheckout={withAccessCodeValidation(handleOnCheckout)}
      onContinue={onContinue && withAccessCodeValidation(onContinue)}
      onMonthChange={handleMonthChange}
      onRangeChange={handleStayChange}
      onSessionSelect={handleSessionChange}
      onTierSelect={setTierId}
      selectedPriceTierId={tierId}
      stayPricing={stayPricing}
    />
  );
};
